/**
 * Identidad visual de cada integrante.
 *
 * El color es el que responde «¿de quién es esto?» sin necesidad de leyenda:
 * un punto ámbar en una lista, en una barra o en una gráfica siempre es la
 * misma persona. Por eso el token se asigna al unirse al hogar y la base lo
 * fuerza único dentro de cada hogar — dos integrantes del mismo color
 * volverían ilegible cualquier vista comparativa.
 *
 * El orden importa: los dos primeros son ámbar y turquesa, que son los que verá
 * una pareja. Es el par que mejor se distingue bajo las tres formas de
 * daltonismo, a diferencia del rojo–verde habitual, que desaparece para casi
 * uno de cada doce hombres.
 *
 * Ninguno es verde ni rojo puro, porque esos dos ya significan «entra» y «sale»
 * dinero; ni azul, que es el color de la marca y la navegación. Un punto de
 * color solo puede querer decir una cosa.
 */

export const PERSON_TOKENS = [
  'ambar',
  'turquesa',
  'violeta',
  'rosa',
  'cian',
  'lima',
  'cobre',
  'purpura',
] as const

export type PersonToken = (typeof PERSON_TOKENS)[number]

interface PersonPalette {
  token: PersonToken
  /** Nombre visible al elegir color en Ajustes. */
  label: string
  /** Clases de Tailwind ligadas a las fichas del tema. */
  fill: string
  text: string
  soft: string
  border: string
  ring: string
  /** Variable CSS cruda, para SVG de gráficas donde no llega Tailwind. */
  cssVar: string
  /** El mismo tono en segundo plano. El ahorro se dibuja con este. */
  pastelVar: string
}

const PALETTES: Record<PersonToken, PersonPalette> = {
  ambar: {
    token: 'ambar',
    label: 'Ámbar',
    fill: 'bg-person-1',
    text: 'text-person-1-text',
    soft: 'bg-person-1-soft',
    border: 'border-person-1',
    ring: 'ring-person-1',
    cssVar: 'var(--person-1-fill)',
    pastelVar: 'var(--person-1-pastel)',
  },
  turquesa: {
    token: 'turquesa',
    label: 'Turquesa',
    fill: 'bg-person-2',
    text: 'text-person-2-text',
    soft: 'bg-person-2-soft',
    border: 'border-person-2',
    ring: 'ring-person-2',
    cssVar: 'var(--person-2-fill)',
    pastelVar: 'var(--person-2-pastel)',
  },
  violeta: {
    token: 'violeta',
    label: 'Violeta',
    fill: 'bg-person-3',
    text: 'text-person-3-text',
    soft: 'bg-person-3-soft',
    border: 'border-person-3',
    ring: 'ring-person-3',
    cssVar: 'var(--person-3-fill)',
    pastelVar: 'var(--person-3-pastel)',
  },
  rosa: {
    token: 'rosa',
    label: 'Rosa mexicano',
    fill: 'bg-person-4',
    text: 'text-person-4-text',
    soft: 'bg-person-4-soft',
    border: 'border-person-4',
    ring: 'ring-person-4',
    cssVar: 'var(--person-4-fill)',
    pastelVar: 'var(--person-4-pastel)',
  },
  cian: {
    token: 'cian',
    label: 'Cian',
    fill: 'bg-person-5',
    text: 'text-person-5-text',
    soft: 'bg-person-5-soft',
    border: 'border-person-5',
    ring: 'ring-person-5',
    cssVar: 'var(--person-5-fill)',
    pastelVar: 'var(--person-5-pastel)',
  },
  lima: {
    token: 'lima',
    label: 'Lima',
    fill: 'bg-person-6',
    text: 'text-person-6-text',
    soft: 'bg-person-6-soft',
    border: 'border-person-6',
    ring: 'ring-person-6',
    cssVar: 'var(--person-6-fill)',
    pastelVar: 'var(--person-6-pastel)',
  },
  cobre: {
    token: 'cobre',
    label: 'Cobre',
    fill: 'bg-person-7',
    text: 'text-person-7-text',
    soft: 'bg-person-7-soft',
    border: 'border-person-7',
    ring: 'ring-person-7',
    cssVar: 'var(--person-7-fill)',
    pastelVar: 'var(--person-7-pastel)',
  },
  purpura: {
    token: 'purpura',
    label: 'Púrpura',
    fill: 'bg-person-8',
    text: 'text-person-8-text',
    soft: 'bg-person-8-soft',
    border: 'border-person-8',
    ring: 'ring-person-8',
    cssVar: 'var(--person-8-fill)',
    pastelVar: 'var(--person-8-pastel)',
  },
}

export function personPalette(token: PersonToken | string | null | undefined): PersonPalette {
  if (token && token in PALETTES) return PALETTES[token as PersonToken]
  return PALETTES.ambar
}

/** Primer color libre del hogar, para asignar al siguiente que se una. */
export function nextAvailableToken(taken: readonly string[]): PersonToken {
  const used = new Set(taken)
  return PERSON_TOKENS.find((t) => !used.has(t)) ?? PERSON_TOKENS[0]
}

export function allPalettes(): PersonPalette[] {
  return PERSON_TOKENS.map((t) => PALETTES[t])
}

/**
 * Iniciales para el avatar. Toma la primera letra del nombre y del apellido;
 * con un solo nombre usa solo esa, en vez de partir la palabra por la mitad.
 */
export function initials(displayName: string): string {
  const parts = displayName.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0]!.charAt(0).toUpperCase()
  return (parts[0]!.charAt(0) + parts[parts.length - 1]!.charAt(0)).toUpperCase()
}
